import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { BoundedContent, GridContainer, Column, Row } from '@vp/swan';
import { ModBoss, ModTarget } from '@vp/mod-father';
import useSiteData from '../hooks/useSiteData';
import { modfatherTracker as hostTracker } from '../apis/trackingApi';
import { CartModWrapper } from './uxMods/CartModWrapper';
import { cartItemSelectors } from '../state/cartItems';
import { EmptyCartDisplay } from './emptyCart/EmptyCartDisplay';
import { CartLayout } from './layout/CartLayout';
import { cartSelectors } from '../state/cart';
import { CartUnavailableDisplay } from './cartUnavailable/CartUnavailableDisplay';
import useIdentityContext from '../hooks/useIdentityContext';
import useFeature from '../hooks/useFeature';
import useExperiment from '../hooks/useExperiment';
import useTracking from '../hooks/useTracking';

export const CartDisplay = () => {
  const {
    uiLocalization: { locale },
    allowAnonymousCheckoutSessions,
    enabledExperimentKeys,
  } = useSiteData();

  const isCartEmpty = useSelector(
    state => !cartItemSelectors.selectHasItems(state.cartItems)
  );
  const cToolsDown = useSelector(state =>
    cartSelectors.selectIsCToolsOffline(state.cart)
  );
  const blockUserInteraction = useSelector(state =>
    cartItemSelectors.selectAreAnyItemsBlockingUserInteraction(state)
  );
  const notReadyToEnterCheckout = useSelector(
    state => !cartSelectors.selectReadyToEnterCheckout(state.cart)
  );
  const hasItems = useSelector(state =>
    cartItemSelectors.selectHasItems(state.cartItems)
  );
  const isEligibleForAnonymousPurchase = useSelector(state =>
    cartSelectors.selectIsEligibleForAnonymousPurchase(state.cart)
  );

  // if cart has items but is not ready to enter checkout
  const trackCartCheckoutBlocked = notReadyToEnterCheckout && hasItems;

  // If pricing, item is invalid, or item's cart template does not exist: disable checkout button and display error messaging
  // If just blockUserInteraction temporarily disable checkout button
  const checkoutLinkIsDisabled =
    notReadyToEnterCheckout || blockUserInteraction;

  // Detect when we come to this page but cannot check out, and track.
  const tracker = useTracking();
  useEffect(() => {
    if (trackCartCheckoutBlocked) {
      tracker.checkoutDisabledExperience();
    }
    // Disable this rule because 'tracker' instance changes each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trackCartCheckoutBlocked]);

  const { identity } = useIdentityContext();

  const offerCartSignInGuestCheckoutExperience = useFeature(
    'offerCartSignInGuestCheckoutExperience'
  );

  const siteEnablesGuestCheckoutExperiment =
    enabledExperimentKeys?.includes('guest_checkout');
  const experimentAllowsGuestCheckout = useExperiment(
    'guest_checkout',
    'variation_2',
    !!(identity.isAnonymousUser && siteEnablesGuestCheckoutExperiment)
  );

  const displaySignInAndCheckoutButton =
    !!identity.isAnonymousUser &&
    allowAnonymousCheckoutSessions &&
    offerCartSignInGuestCheckoutExperience &&
    // if the site has the guest_checkout experiment configured, then only allow guest checkout if user is declaratively in coag_on variant.
    (!siteEnablesGuestCheckoutExperiment || experimentAllowsGuestCheckout) &&
    // only offer guest checkout experience (sign in and checkout + checkout as guest buttons) if the cart is eligible for anonymous purchase
    isEligibleForAnonymousPurchase;

  return (
    <ModBoss
      modHost="cart"
      locale={locale}
      environmentData={{}}
      consoleOutputLogLevel="error"
      hostTracker={hostTracker}
    >
      <CartModWrapper>
        <BoundedContent marginBottom={12} style={{ maxWidth: '1440px' }}>
          <GridContainer>
            {cToolsDown ? (
              <CartUnavailableDisplay />
            ) : (
              <>
                {isCartEmpty && <EmptyCartDisplay />}
                {!isCartEmpty && (
                  <CartLayout
                    displaySignInAndCheckoutButton={
                      displaySignInAndCheckoutButton
                    }
                    checkoutDisabled={notReadyToEnterCheckout}
                    checkoutLinkDisabled={checkoutLinkIsDisabled}
                  />
                )}
                <Row mt="4">
                  <Column span={12}>
                    <ModTarget name="belowCart" />
                  </Column>
                </Row>
              </>
            )}
          </GridContainer>
        </BoundedContent>
      </CartModWrapper>
    </ModBoss>
  );
};
